import * as React from 'react';

import styled, {CSS, CSSProperties} from '@pinecast/styles';

import {DEFAULT_FONT} from './constants';
import TextInputWrapper from './TextInputWrapper';

export type Sizes = 'normal' | 'large';

const Input = styled(
  'input',
  (
    {$hasPrefix, $size = 'normal'}: {$hasPrefix: boolean; $size?: Sizes},
    designColors,
  ) => ({
    backgroundColor: 'transparent',
    border: 0,
    borderBottomLeftRadius: $hasPrefix ? 0 : 4,
    borderBottomRightRadius: 4,
    borderTopLeftRadius: $hasPrefix ? 0 : 4,
    borderTopRightRadius: 4,
    color: designColors.contentTextAccent,
    flex: '1 1',
    fontFamily: DEFAULT_FONT,
    fontSize: $size === 'normal' ? 14 : 20,
    gridRow: '1 / 1',
    height: '100%',
    letterSpacing: 'inherit',
    maxWidth: '100%',
    padding: '0 10px',
    position: 'relative',
    transition: 'box-shadow 0.2s, opacity 0.2s',
    width: '100%',
    zIndex: 2,

    ':focus': {outline: 'none'},

    ':not(:last-child)': {
      borderBottomRightRadius: 0,
      borderTopRightRadius: 0,
    },

    '::placeholder': {color: '#c6caca'},
    '::-webkit-placeholder': {color: '#c6caca'},
  }),
  {className: 'TextInput', dir: 'auto'},
);

export interface Props {
  'aria-label'?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  endCapStyle?: CSS;
  inputStyle?: CSS;
  invalid?: boolean;
  maxLength?: number;
  minLength?: number;
  name?: string;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLInputElement>['onBlur'];
    onChange: React.HTMLAttributes<HTMLInputElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLInputElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLInputElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLInputElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLInputElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLInputElement>['onKeyPress'];
  }>;
  onChange?: (value: string) => void;
  pattern?: string;
  placeholder?: string;
  prefix?: React.ReactNode;
  readOnly?: boolean;
  required?: boolean;
  size?: Sizes;
  style?: CSSProperties;
  suffix?: React.ReactNode;
  tabIndex?: number;
  type?: 'email' | 'text' | 'url' | 'password' | 'number' | 'tel';
  useWrapper?: boolean;
  value: string;
}

const TextInput = (
  {
    'aria-label': ariaLabel,
    autoComplete,
    endCapStyle,
    inputStyle,
    invalid,
    onChange,
    nativeEvents,
    prefix,
    size = 'normal',
    style,
    suffix,
    type = 'text',
    useWrapper = true,
    value,
    ...rest
  }: Props,
  ref: React.Ref<HTMLInputElement>,
) => {
  const {maxLength} = rest;
  const nativeOnChange = nativeEvents?.onChange;
  const handleChange = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      nativeOnChange?.(e);
      if (!onChange) {
        return;
      }
      const value = (e.target as HTMLInputElement).value;
      if (maxLength && value.length > maxLength) {
        onChange(value.substr(0, maxLength));
      } else {
        onChange(value);
      }
    },
    [nativeOnChange, onChange, maxLength],
  );
  const input = (
    <Input
      aria-label={ariaLabel}
      autoComplete={autoComplete}
      $hasPrefix={Boolean(prefix)}
      $size={size}
      style={inputStyle}
      {...rest}
      ref={ref}
      type={type}
      {...nativeEvents}
      onChange={handleChange}
      value={value}
    />
  );
  if (!useWrapper) {
    return input;
  }
  return (
    <TextInputWrapper
      disabled={!!rest.disabled}
      endCapStyle={endCapStyle}
      invalid={!!invalid}
      prefix={prefix}
      size={size}
      style={style}
      suffix={suffix}
    >
      {input}
    </TextInputWrapper>
  );
};

export default React.forwardRef<HTMLInputElement, Props>(TextInput);
